import { getStationaryBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import {
  BookingSummaryLoadingBottomBar,
  BookingSummaryScreenBody
} from '@components/BookingFlow/Common/BookingSummaryScreenBody';
import { getBookingSummaryTitle } from '@components/BookingFlow/Common/getBookingSummaryTitle';
import { StationaryBookingSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { NavioContactChatWidget } from '@components/BookingFlow/Common/NavioContactChatWidget';
import { OrganizationBookingAssignee } from '@components/BookingFlow/Common/OrganizationBookingAssignee';
import { OrganizationCarInvoiceReference } from '@components/BookingFlow/Common/OrganizationCarInvoiceReference';
import { StationaryBookingNextStepsFromStore } from '@components/BookingFlow/Stationary/StationaryBookingNextSteps';
import { StationaryBookingScreen } from '@components/BookingFlow/Stationary/StationaryBookingScreen';
import { StationaryCheckoutChrome } from '@components/BookingFlow/Stationary/StationaryCheckoutChrome';
import { StationaryNonMemberOwnerNotice } from '@components/BookingFlow/Stationary/StationaryNonMemberOwnerNotice';
import { StationaryOrderSummaryCard } from '@components/BookingFlow/Stationary/StationaryOrderSummaryCard';
import { StationaryPaymentChoice } from '@components/BookingFlow/Stationary/StationaryPaymentChoice';
import { StationaryReservationExpiredBanner } from '@components/BookingFlow/Stationary/StationaryReservationExpiredBanner';
import { TireHotelAtOtherDepartmentPrompt } from '@components/BookingFlow/Stationary/TireHotelAtOtherDepartmentPrompt';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { SilencedCarManagersWarning } from '@components/Summary/SilencedCarManagersWarning';
import { PaymentOptionsInstructions } from '@components/TireOffer/PaymentOptionsInstructions';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { UserGroupDataQualityPromptCustomer } from '@components/UserGroupDataQuality/UserGroupDataQualityPromptCustomer';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType, PaymentOption } from '@navio/nsdk';
import { FixedBottomBarContainer, NoddiCheckbox, NoddiLink } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useCancelHoldOnExpiry } from 'noddi-web';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useCustomerDepartmentSelection } from '../../../../hooks/useCustomerDepartmentSelection';
import { useStationarySummaryData } from '../../../../hooks/useStationarySummaryData';
import { useStationaryIncomingTireSeason } from '../../../../stores/StationaryBookingStore';
import { CarManagersInfo } from './CarManagersInfo';
import { ConfirmStationaryBooking } from './ConfirmStationaryBooking';
import { ExistingBookingForSeasonCard } from './ExistingBookingForSeasonCard';
import { OrganizationPicker } from './OrganizationPicker';

export const StationaryBookingSummary = () => {
  const { getSelectedDepartmentForGroup } = useCustomerDepartmentSelection();
  const incomingTireSeason = useStationaryIncomingTireSeason();
  const navigate = useNavigate();
  const [invoiceReference, setInvoiceReference] = useState('');
  const {
    isLoggedIn,
    isSummaryLoading,
    summaryError,
    retrySummary,
    isSummaryRetrying,
    serviceCenter,
    reservation,
    lineItems,
    totalPrice,
    paymentOption,
    setPaymentOption,
    isInvoice,
    showPaymentChoice,
    invoiceTo,
    invoiceRecipientName,
    invoiceReferenceRule,
    invoiceToUserGroupId,
    nonMemberOwnerNotice,
    hasMultipleOrganizations,
    requiresOrganizationNumber,
    organizationsForCar,
    selectedOrganization,
    setSelectedOrganization,
    organizationName,
    registrationNumber,
    serviceOrganization,
    locationAddress,
    carDisplayName,
    existingBookingForSeason,
    hasValidUserPhone,
    isLandline,
    invalidUserPhoneDisplay,
    customerName,
    currentUserGroup,
    memberUserGroupId,
    bookingParty,
    organizationUserGroupId,
    carManagers,
    silencedCarManagers,
    smsOptIns,
    isBookerOptedIn,
    setBookerOptIn,
    isBookerTexted,
    bookingAssignee
  } = useStationarySummaryData();

  // Keep credentials after client TTL so re-reserve can cancel-before-hold.
  const isReservationExpired = useCancelHoldOnExpiry(reservation);
  const needsNewTimeSlot = !reservation || isReservationExpired;
  const timeWindowPath = customerRoutes.newStationaryBookingTimeWindow.getPath();

  // Login is its own step, so this page is review-and-pay only.
  if (!isLoggedIn) {
    return <Navigate to={customerRoutes.newStationaryBookingLogin.getPath()} replace />;
  }

  // Keep the step chrome (title / bottom bar) mounted while data settles.
  if (isSummaryLoading || summaryError) {
    return (
      <StationaryBookingScreen
        progress={getStationaryBookingProgress('summary')}
        title={getBookingSummaryTitle()}
        customBottomBar={<BookingSummaryLoadingBottomBar />}
      >
        <BookingSummaryScreenBody error={summaryError} onRetry={retrySummary} isRetrying={isSummaryRetrying} />
      </StationaryBookingScreen>
    );
  }

  const showCustomerIdentity = isLoggedIn && !!currentUserGroup;

  return (
    <CustomerDataQualityProvider
      userGroupId={memberUserGroupId}
      bookingLocationType={BookingLocationType.STATIONARY}
      countryCode={serviceOrganization?.address?.countryCode}
    >
      <StationaryBookingScreen
        progress={getStationaryBookingProgress('summary')}
        title={getBookingSummaryTitle()}
        customBottomBar={
          <FixedBottomBarContainer>
            <div className='flex items-center gap-2'>
              <FlowBackButton />
              <NavioContactChatWidget />
            </div>
            {needsNewTimeSlot ? (
              <NoddiLink to={timeWindowPath} variant='success'>
                <Trans>Choose a new time</Trans>
              </NoddiLink>
            ) : (
              <ConfirmStationaryBooking
                paymentOption={paymentOption}
                invoiceReference={
                  invoiceReferenceRule
                    ? { value: invoiceReference, required: invoiceReferenceRule.required }
                    : undefined
                }
                userGroupPayerId={invoiceToUserGroupId}
                totalAmount={totalPrice}
                hasExistingBooking={!!existingBookingForSeason}
                hasValidPhone={!isLoggedIn || hasValidUserPhone}
                hasOrganizationNumber={!requiresOrganizationNumber}
                bookingUserGroupId={bookingParty.userGroupId}
                bookingUserId={bookingAssignee.selectedUserId}
                notificationsToOverride={smsOptIns.notificationsToOverride}
              />
            )}
          </FixedBottomBarContainer>
        }
      >
        <div className='flex flex-col gap-4'>
          <StationaryBookingSpikedTireSeasonBanner />
          <UserGroupDataQualityPromptCustomer />
          <TireHotelAtOtherDepartmentPrompt
            // oxlint-disable-next-line typescript/no-misused-promises -- async onSwitched runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onSwitched={() => navigate(customerRoutes.newStationaryBookingServices.getPath())}
          />

          {needsNewTimeSlot && <StationaryReservationExpiredBanner timeWindowPath={timeWindowPath} />}

          {isLoggedIn && existingBookingForSeason && (
            <ExistingBookingForSeasonCard booking={existingBookingForSeason} />
          )}

          <StationaryCheckoutChrome
            isLoggedIn={isLoggedIn}
            hasValidBookingPhone={hasValidUserPhone}
            isLandline={isLandline}
            invalidPhoneDisplay={invalidUserPhoneDisplay}
            organizationName={organizationName || invoiceRecipientName}
            registrationNumber={registrationNumber}
            invoiceToName={invoiceTo?.name}
            showInvoiceBanner={isInvoice && !showPaymentChoice}
            requiresOrganizationNumber={requiresOrganizationNumber}
            betweenAddressAndInvoice={
              isLoggedIn &&
              hasMultipleOrganizations && (
                <OrganizationPicker
                  organizations={organizationsForCar!}
                  selectedOrganization={selectedOrganization}
                  onSelect={setSelectedOrganization}
                />
              )
            }
          >
            <CarManagersInfo carManagers={carManagers} />
            {organizationUserGroupId != null && (
              <div className='flex flex-col gap-2'>
                <SilencedCarManagersWarning
                  userGroupId={organizationUserGroupId}
                  silencedManagers={silencedCarManagers}
                  smsOptIns={smsOptIns}
                />
                <NoddiCheckbox
                  id='notify-booker'
                  checked={isBookerOptedIn}
                  onCheckedChange={setBookerOptIn}
                  label={t`Send me SMS updates about this booking`}
                />
              </div>
            )}
          </StationaryCheckoutChrome>

          <StationaryOrderSummaryCard
            customerName={showCustomerIdentity ? customerName : undefined}
            organizationName={isLoggedIn ? (bookingParty.organizationName ?? undefined) : undefined}
            departmentName={
              isLoggedIn && bookingParty.userGroupId
                ? getSelectedDepartmentForGroup(bookingParty.userGroupId)?.name
                : undefined
            }
            locationName={serviceCenter?.name ?? ''}
            locationAddress={locationAddress}
            timeWindow={reservation ? { startsAt: reservation.startsAt, endsAt: reservation.endsAt } : undefined}
            reservedUntil={reservation?.reservedUntil}
            carDisplayName={carDisplayName}
            incomingTireSeason={incomingTireSeason}
            lineItems={lineItems}
            totalPrice={{ amount: totalPrice }}
          />

          {nonMemberOwnerNotice && <StationaryNonMemberOwnerNotice notice={nonMemberOwnerNotice} />}

          <OrganizationBookingAssignee
            assignee={bookingAssignee.assignee}
            selectedUserId={bookingAssignee.selectedUserId}
            onSelect={bookingAssignee.selectUserId}
          />

          {showPaymentChoice && (
            <StationaryPaymentChoice
              paymentOption={paymentOption}
              onSelect={setPaymentOption}
              invoiceRecipientName={invoiceRecipientName}
            />
          )}

          <OrganizationCarInvoiceReference
            rule={invoiceReferenceRule}
            value={invoiceReference}
            onChange={setInvoiceReference}
          />

          {paymentOption === PaymentOption.PAY_BEFORE && totalPrice > 0 && <PaymentOptionsInstructions />}

          {isLoggedIn && <StationaryBookingNextStepsFromStore isBookerTexted={isBookerTexted} />}
        </div>
      </StationaryBookingScreen>
    </CustomerDataQualityProvider>
  );
};
